import * as React from "react";

import {
  StatsigProvider,
  StatsigUser,
  useStatsigUser,
} from "@statsig/react-bindings";

import { useProfileStore } from "@/store/profile.store";
import { getCookie } from "@/utils/cookies.utils";

import { getStatsigEnvironment } from "./util/statsig.utils";

type StatsigReactProviderProps = {
  children: React.ReactNode;
};

/**
 * Wrapper around the react sdk StatsigProvider which provides the statsig context to the app and instantiates the StatsigUser.
 */
const StatsigReactProvider = ({ children }: StatsigReactProviderProps) => {
  const { profile } = useProfileStore();
  const { updateUserAsync } = useStatsigUser();

  const initialAnonymousId = getCookie("ajs_anonymous_id");

  const initialStatsigUser: StatsigUser = {
    userID: profile?.SSOid,
    customIDs: { segmentAnonymousID: initialAnonymousId },
    custom: {
      isHost: !!profile?.is_host || false,
      userLocale: profile ? profile?.preferred_locale || "en-US" : undefined,
    },
  };

  const statsigEnvironment = getStatsigEnvironment();

  React.useEffect(() => {
    if (!profile?.SSOid) return;

    const anonymousId = getCookie("ajs_anonymous_id");

    const newStatsigUser = {
      userID: profile?.SSOid,
      customIDs: {
        segmentAnonymousID: anonymousId,
      },
      custom: {
        isHost: profile?.is_host || false,
        userLocale: profile?.preferred_locale || "en-US",
      },
    };

    updateUserAsync(newStatsigUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.SSOid, profile?.is_host, profile?.preferred_locale]);

  return (
    <StatsigProvider
      sdkKey={window.PUBLIC_ENV.STATSIG_SDK_KEY}
      user={initialStatsigUser}
      options={{
        environment: {
          tier: statsigEnvironment,
        },
        loggingEnabled: window.isBot ? "disabled" : "browser-only", // disabled for bots to avoid unnecessary overhead - "browser-only" is the default value
        networkConfig: { preventAllNetworkTraffic: window.isBot }, // prevent any network calls for bots, including to Statsig's servers
      }}
    >
      {children}
    </StatsigProvider>
  );
};

export { StatsigReactProvider, type StatsigReactProviderProps };
